const TermsAndConditionsForEmployers = () => (
  <div className="flex flex-col gap-2 text-sm font-inter">
    <h2 className="mb-2">Terms & Conditions for Companies</h2>
    <div className="px-0 md:px-6">
      <p>
        <strong>Effective Date:</strong> 29 April 2025
      </p>
      <p className="mt-4">
        These Terms & Conditions ("Terms") govern the access to and use of the
        Qureos platform by companies and their authorized representatives.
        Qureos Inc. ("Qureos," "we," "our," or "us") provides its recruitment
        software, services, and tools subject to these Terms. They outline your
        rights and responsibilities when using our services and describe the
        protections Qureos applies to your information and activities.
      </p>
      <p className="mt-4">
        By registering a Company account, subscribing to a plan, or using our
        Platform in any way, you agree to these Terms. We recommend you review
        them carefully, as they create a legally binding agreement between your
        Company and Qureos.
      </p>
    </div>

    <h3 className="mt-8">1. Definitions</h3>
    <div className="px-0 md:px-6">
      <p>
        To ensure clear understanding, the following definitions apply
        throughout these Terms:
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>"Company"</strong> refers to the legal entity subscribing to
          or accessing the Qureos Platform to recruit or manage talent.
          <p className="mt-2">
            This includes any entity using the Platform to search for
            candidates, post jobs, or interact with talent.
          </p>
        </li>
        <li className="mb-4">
          <strong>"Representative"</strong> means any employee or authorized
          agent acting on behalf of the Company.
          <p className="mt-2">
            Representatives are bound by these Terms when acting on behalf of
            the Company.
          </p>
        </li>
        <li className="mb-4">
          <strong>"Platform"</strong> means the software, website (Qureos.com),
          applications, APIs, and services provided by Qureos.
          <p className="mt-2">
            The Platform enables Companies to post jobs, search for candidates,
            communicate, and manage recruitment workflows.
          </p>
        </li>
        <li className="mb-4">
          <strong>"Subscription Services"</strong> refer to the paid access
          Companies obtain to use Qureos functionalities.
          <p className="mt-2">
            Different plans may include different feature access or usage
            limits.
          </p>
        </li>
        <li className="mb-4">
          <strong>"Candidate"</strong> refers to any individual seeking
          employment opportunities through Qureos.
          <p className="mt-2">
            Candidates create profiles, apply for jobs, and engage with Company
            postings.
          </p>
        </li>
      </ul>
      <p>
        These definitions provide the framework for understanding your use of
        our Platform.
      </p>
    </div>

    <h3 className="mt-8">2. Scope of Subscription Services</h3>
    <div className="px-0 md:px-6">
      <p>
        When your Company subscribes to Qureos, you receive access to specific
        services and tools based on your subscription plan. This section
        outlines what is included and the limitations.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Access to Platform:</strong> Subscriptions grant a limited,
          non-exclusive, non-transferable right to access and use Qureos
          services.
          <p className="mt-2">
            Your access is restricted to the number of seats, users, or usage
            limits defined in your plan.
          </p>
        </li>
        <li className="mb-4">
          <strong>Service Features:</strong> Subscription Services may include
          candidate sourcing, job posting, communication tools, analytics, and
          reporting.
          <p className="mt-2">
            Features vary by plan level and may evolve as Qureos updates the
            platform.
          </p>
        </li>
        <li className="mb-4">
          <strong>Restrictions:</strong> Subscription Services are intended only
          for internal hiring and recruitment purposes.
          <p className="mt-2">
            Competitive analysis, benchmarking, or resale of data is prohibited.
          </p>
        </li>
      </ul>
      <p>
        Understanding the scope helps Companies effectively utilize their
        subscription benefits while staying within permitted uses.
      </p>
    </div>

    <h3 className="mt-8">3. Payment Terms and Billing</h3>
    <div className="px-0 md:px-6">
      <p>
        Payment terms define how and when Companies are billed for Subscription
        Services. Ensuring clarity in billing supports a smooth customer
        experience.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Billing Cycles:</strong> Companies may choose monthly or
          annual billing.
          <p className="mt-2">
            Monthly subscriptions must be paid via card; annual subscriptions
            may be paid via card or wire transfer.
          </p>
        </li>
        <li className="mb-4">
          <strong>Auto-Renewal:</strong> Subscriptions automatically renew
          unless cancelled with at least 30 days' notice before renewal.
          <p className="mt-2">Qureos provides reminders prior to renewal.</p>
        </li>
        <li className="mb-4">
          <strong>Late Payment:</strong> If payment is not received within 30
          days, service access may be suspended.
          <p className="mt-2">
            Accounts are held for 60 days before final termination if payment is
            not rectified.
          </p>
        </li>
        <li className="mb-4">
          <strong>Refund Policy:</strong> Refunds are granted only if Qureos
          fails to deliver services materially as described.
          <p className="mt-2">
            No refunds are provided for partially used subscription periods if
            services were properly delivered.
          </p>
        </li>
      </ul>
      <p>
        Clear billing structures prevent confusion and maintain a stable service
        relationship.
      </p>
    </div>

    <h3 className="mt-8">4. Company Responsibilities</h3>
    <div className="px-0 md:px-6">
      <p>
        Companies must meet certain responsibilities to ensure lawful,
        compliant, and secure use of the Platform.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Compliance with Laws:</strong> Companies must comply with
          applicable laws when using the Platform.
          <p className="mt-2">
            This includes data protection, employment, and anti-discrimination
            laws.
          </p>
        </li>
        <li className="mb-4">
          <strong>Authorized Access:</strong> Only authorized Representatives
          may use the Platform.
          <p className="mt-2">
            Credentials must not be shared among multiple users unless licensed.
          </p>
        </li>
        <li className="mb-4">
          <strong>Content Accuracy:</strong> Job postings, company profiles, and
          communication must be truthful and not misleading.
          <p className="mt-2">
            False or inaccurate content harms both Candidates and platform
            trust.
          </p>
        </li>
      </ul>
      <p>
        Adhering to these responsibilities ensures a professional and effective
        hiring environment.
      </p>
    </div>

    <h3 className="mt-8">5. Usage Restrictions</h3>
    <div className="px-0 md:px-6">
      <p>
        Certain activities are strictly prohibited to protect the Platform's
        integrity and ensure fair usage for all participants.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>No Reverse Engineering:</strong> Companies may not attempt to
          deconstruct or replicate the Platform's underlying technology.
        </li>
        <li className="mb-4">
          <strong>No Unauthorized Benchmarking:</strong> Companies must not use
          Qureos to build competing products or services.
        </li>
        <li className="mb-4">
          <strong>No Unauthorized Data Use:</strong> Scraping, copying, or
          distributing Candidate data without consent is strictly prohibited.
        </li>
      </ul>
      <p>
        Maintaining responsible usage protects the value of Qureos for all
        users.
      </p>
    </div>

    <h3 className="mt-8">6. Suspension and Service Interruptions</h3>
    <div className="px-0 md:px-6">
      <p>
        Qureos may suspend access to the Platform under specific conditions to
        protect platform security, ensure compliance, or address technical
        threats.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Security Threats:</strong> Immediate suspension may occur if
          your use threatens the security or performance of the Platform.
        </li>
        <li className="mb-4">
          <strong>Non-Payment:</strong> Accounts overdue by more than 30 days
          may be suspended until payment is made.
        </li>
        <li className="mb-4">
          <strong>Violation of Terms:</strong> Breaches of acceptable use
          policies may lead to temporary or permanent suspension.
        </li>
      </ul>
      <p>
        Suspension policies safeguard platform integrity and ensure reliable
        service for compliant users.
      </p>
    </div>

    <h3 className="mt-8">7. Data Protection and Privacy</h3>
    <div className="px-0 md:px-6">
      <p>
        We are committed to protecting the personal data shared on our Platform
        in accordance with GDPR and other applicable regulations.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Data Controller and Processor Roles:</strong> Qureos acts as
          the data processor for Candidate data accessed by Companies.
        </li>
        <li className="mb-4">
          <strong>Data Processing Limits:</strong> Companies may only use
          Candidate data for recruitment purposes.
        </li>
        <li className="mb-4">
          <strong>Confidentiality Requirements:</strong> Companies must protect
          Candidate data using appropriate technical and organizational
          measures.
        </li>
      </ul>
      <p>
        Ensuring responsible data handling reinforces user trust and legal
        compliance.
      </p>
    </div>

    <h3 className="mt-8">8. Intellectual Property</h3>
    <div className="px-0 md:px-6">
      <p>
        All intellectual property rights in the Platform remain with Qureos or
        its licensors.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Ownership:</strong> Qureos retains full ownership of the
          software, content, and technologies.
        </li>
        <li className="mb-4">
          <strong>License Grant:</strong> Companies receive a limited right to
          use the Platform during the active subscription period.
        </li>
        <li className="mb-4">
          <strong>Restrictions:</strong> Companies may not copy, modify, or
          sublicense any aspect of the Platform.
        </li>
      </ul>
      <p>
        Respecting intellectual property safeguards innovation and continuous
        platform improvement.
      </p>
    </div>

    <h3 className="mt-8">9. Disclaimers and Warranties</h3>
    <div className="px-0 md:px-6">
      <p>
        Qureos provides services "as is," and disclaims certain warranties to
        the extent allowed by law.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Service Availability:</strong> We aim for high uptime but do
          not guarantee uninterrupted services.
        </li>
        <li className="mb-4">
          <strong>Accuracy of Data:</strong> Qureos does not guarantee the
          completeness or accuracy of Candidate information.
        </li>
        <li className="mb-4">
          <strong>Third-Party Integrations:</strong> Qureos is not responsible
          for the availability or performance of third-party services.
        </li>
      </ul>
      <p>
        These disclaimers establish realistic expectations regarding platform
        performance.
      </p>
    </div>

    <h3 className="mt-8">10. Limitation of Liability</h3>
    <div className="px-0 md:px-6">
      <p>
        Qureos' liability is limited to ensure fair and proportional risk
        exposure for both parties.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Cap on Damages:</strong> Our liability is capped at the amount
          paid by the Company in the preceding 12 months.
        </li>
        <li className="mb-4">
          <strong>Exclusion of Consequential Damages:</strong> We are not liable
          for indirect losses like lost profits or reputational harm.
        </li>
      </ul>
      <p>
        Clear limitations provide predictability and balance in the service
        relationship.
      </p>
    </div>

    <h3 className="mt-8">11. Term and Termination</h3>
    <div className="px-0 md:px-6">
      <p>
        These Terms remain in effect while your Company has an active
        subscription.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Termination by Company:</strong> Companies may terminate with
          30 days' prior written notice.
        </li>
        <li className="mb-4">
          <strong>Termination by Qureos:</strong> We may terminate for material
          breach or non-payment after notice.
        </li>
        <li className="mb-4">
          <strong>Post-Termination Access:</strong> Upon termination, access
          ceases immediately and outstanding fees become due.
        </li>
      </ul>
      <p>
        Termination provisions allow both parties to manage the service
        relationship with clarity and fairness.
      </p>
    </div>

    <h3 className="mt-8">12. Force Majeure</h3>
    <div className="px-0 md:px-6">
      <p>
        Neither party is liable for failure to perform due to circumstances
        beyond reasonable control.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Examples:</strong> Natural disasters, war, terrorism, labor
          disputes, or internet outages.
        </li>
        <li className="mb-4">
          <strong>Impact:</strong> Affected obligations are suspended for the
          duration of the force majeure event.
        </li>
      </ul>
      <p>
        Force majeure clauses account for extraordinary events that might affect
        platform operations.
      </p>
    </div>

    <h3 className="mt-8">13. General Terms</h3>
    <div className="px-0 md:px-6">
      <p>
        This section outlines legal housekeeping terms that support the
        functioning of this agreement.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Assignment:</strong> Companies may not assign their rights or
          obligations without Qureos' prior written consent.
        </li>
        <li className="mb-4">
          <strong>Severability:</strong> If a provision is found unenforceable,
          the rest of the Terms remain valid.
        </li>
        <li className="mb-4">
          <strong>Governing Law:</strong> These Terms are governed by the laws
          of the UAE (Dubai Development Authority) and Saudi Arabia.
        </li>
      </ul>
      <p>
        General terms help ensure the agreement remains effective even if parts
        are challenged.
      </p>
    </div>

    <h3 className="mt-8">14. Contact Us</h3>
    <div className="px-0 md:px-6">
      <p>
        For questions, support, or legal communications related to these Terms,
        please contact:
      </p>
      <p>
        <strong>Qureos Inc.</strong>
      </p>
      <ul className="list-disc pl-5">
        <li>
          Email:{' '}
          <a href="mailto:hello@qureos.com" className="text-qureosPrimary">
            hello@qureos.com
          </a>
        </li>
        <li>Jurisdiction: UAE (Dubai Development Authority) and KSA</li>
      </ul>
    </div>
  </div>
)

export default TermsAndConditionsForEmployers
